import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import { useQuery } from 'react-query';
import { NIL } from 'uuid';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { Proposal } from '../models/proposal';
import { parseVenues } from './forms/rnwyVenues';
import { WorkOrder } from '../models/workOrder';
import useProposalApproval from '../hooks/useProposalApproval';
import useUsers from '../hooks/useUsers';
import useCompanyDefaults from '../hooks/useCompanyDefaults';
import { UserDto } from '../models/user';
import { ProjectLeadAssignment } from '../models/project';
import { validateLeads } from '../utils/leadValidation';
import { depositInvoiceName } from '../utils/proposalNaming';
import ReceivedPoAttachments from './ReceivedPoAttachments';
import { money as currency } from '../utils/money'; // item 327 — stealth-aware
import { computeInvoicePlan } from '../hooks/useInvoicePlan';
import InvoiceStepBanner from './InvoiceStepBanner';

// item 611 — the guided approval opens on a PO-number step (the customer's real PO),
// then Confirm → Work order → Deposit invoice. PO entry is OPTIONAL — it never blocks.
const STEPS = ['PO number', 'Confirm', 'Work order', 'Deposit invoice'] as const;

interface ProposalApprovalWizardProps {
  isOpen: boolean;
  onClose: () => void;
  proposal?: Proposal;
}

/**
 * The proposal approval wizard (item 49). MARK APPROVED on a Pending proposal
 * opens this: confirm → approve (backend auto-creates the work order) → show
 * that work order → optionally generate the deposit invoice from it (which
 * flips the proposal to Approved Deposited server-side).
 */
// item 962 — a priced-externally deal's real ONE-TIME total from its manual snapshot
// (largest filled option, incl. custom one-time rows + SwitchPilot config). Pure module
// helper (no hooks) so the wizard can use it before any conditional rendering.
const computeManualOneTime = (json?: string | null): number => {
  if (!json) return 0;
  try {
    const m = JSON.parse(json);
    const extras = (k: string) => (m.extraOneTime ?? [])
      .reduce((t: number, r: any) => t + (Number(r?.[k]) || 0), 0);
    const sp = m.switchPilot?.offered ? (Number(m.switchPilot?.oneTimeConfig) || 0) : 0;
    const sum = (o: any, k: string) => (o
      ? (Number(o.config) || 0) + (Number(o.graphics) || 0) + (Number(o.dev) || 0)
        + (Number(o.licenseOneTime) || 0) + (Number(o.retainBuyIn) || 0) + extras(k) + sp
      : 0);
    return Math.max(sum(m.perpetual, 'perpetual'), sum(m.twoYear, 'twoYear'), sum(m.fiveYear, 'fiveYear'));
  } catch { return 0; }
};

const ProposalApprovalWizard: React.FC<ProposalApprovalWizardProps> = ({
  isOpen, onClose, proposal,
}) => {
  const toast = useToast();
  // item 422/432 — the proposal total. Row shapes lie: the grid sends
  // retailProposalTotal (SQL-summed, correct), but the PROJECT page embeds raw
  // proposal ENTITIES WITHOUT their items — so their computed retailTotal
  // serializes as 0 no matter what the proposal is worth. The only honest source
  // is the proposal DETAIL (items loaded); fetch it while the wizard is open and
  // sum the items, falling back to the row fields until it arrives.
  const { data: detail, status: detailStatus } = useQuery(
    ['proposal', proposal?.id],
    async () => {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      const { data } = await axios.get(`${apiUrl}Proposal/${proposal?.id}`);
      return data;
    },
    { enabled: isOpen && !!proposal?.id && proposal.id !== NIL },
  );
  const detailTotal = Array.isArray(detail?.proposalItems)
    ? detail.proposalItems.reduce(
      (sum: number, it: { retailEach?: number; taxEach?: number; quantity?: number }) => sum
        + ((it.retailEach ?? 0) + (it.taxEach ?? 0)) * (it.quantity ?? 0),
      0,
    )
    : undefined;
  const fallbackTotal = proposal?.retailProposalTotal ?? proposal?.retailTotal;
  const proposalTotal = detailTotal ?? fallbackTotal ?? 0;
  // item 704 (Chad) — an RNWY (engine) proposal's LINE total is $0 until approval materializes it, so
  // the $1k commission floor below wrongly hid the project-lead step. Use the engine amount (perpetual,
  // else 2yr/5yr) as the commission base so leads prompt on RNWY deals like they do on standard ones.
  // item 962 (Chad: wizard "total $0" on a priced-externally deal) — two bugs in one:
  // (1) the ?? chain let a 0 perpetual rollup poison the base (0 is not null — engine deals
  //     always had perpetual > 0, manual deals often don't); use || so the first POSITIVE wins.
  // (2) manual deals compute their real ONE-TIME total from the snapshot (filled option,
  //     incl. custom one-time rows + SwitchPilot config), since rollup 2yr/5yr are MONTHLY.
  const manualOneTime = computeManualOneTime((proposal as any)?.rnwyManualPricingJson);
  const rnwyBase = manualOneTime > 0
    ? manualOneTime
    : ((proposal as any)?.rnwyPerpetualAmount || (proposal as any)?.rnwyTwoYearAmount
      || (proposal as any)?.rnwyFiveYearAmount || 0);
  const commissionBase = Math.max(proposalTotal, proposal?.isRnwy ? (rnwyBase ?? 0) : 0);
  // item 962 — the DISPLAYED total prefers real line totals, else the deal's engine/manual value
  const displayTotal = (proposalTotal > 0 || !proposal?.isRnwy) ? proposalTotal : rnwyBase;
  // item 458 — never present a confident "$0.00" while the items are still in
  // flight: project-page rows embed proposals WITHOUT items, so their fallback
  // total is a hard 0 until the detail lands (seconds on a slow WAN day — Chad
  // read it as broken money). Loading → spinner; failed → say so out loud.
  const totalPending = detailTotal === undefined
    && detailStatus === 'loading'
    && !(typeof fallbackTotal === 'number' && fallbackTotal > 0);
  const totalFailed = detailTotal === undefined && detailStatus === 'error';
  const {
    approveProposal, getWorkOrderByProposal, generateDepositInvoice, sendApprovalEmail,
  } = useProposalApproval();
  // dart (2026-07-14) — after the deposit invoice is generated, PROMPT to send the thank-you
  // email (with a skip + a "send elsewhere" recipient override) instead of auto-sending.
  const [emailPrompt, setEmailPrompt] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  // item 244 — defer the user-list fetch until the wizard is actually open AND the
  // proposal clears the $1k commission floor (the only step needing users is the
  // project-lead split, hidden below the floor). Skips a round-trip on small proposals.
  const { data: users } = useUsers(isOpen); // item 1143 — the lead step always needs them
  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.300'); // item 958 — light-mode contrast (whiteAlpha edge vanished on light)

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [workOrder, setWorkOrder] = useState<WorkOrder | undefined>(undefined);
  const [itemize, setItemize] = useState(false); // Chad: default itemize OFF (TODO: make a company setting)
  // item 164: the project leads assigned at approval (N leads + % split) — drive the
  // project-lead commission, split across them by %.
  const [leads, setLeads] = useState<ProjectLeadAssignment[]>([]);
  // item 1174 (Chad) — the guided process can SIGN "no lead commission books on this job",
  // even above the $1k floor; stamped onto the auto-created WO and honored by the accrual.
  const [noLeadCommission, setNoLeadCommission] = useState(false);
  // item 1145 (Chad) — illustrations become a REAL Deployment task with an assignee (the
  // task machinery emails them) + a Sales Bible stamp
  const [illNeeded, setIllNeeded] = useState(false);
  const [illAssignee, setIllAssignee] = useState('');
  // item 240 — for a RNWY proposal, pick the sale type (Perpetual/2yr/5yr) HERE at approval
  const [rnwySaleType, setRnwySaleType] = useState(0);
  // dart (2026-07-15, Chad) — Phase A: the client's Help Desk choice (0 = No / None, else the tier).
  // Prefilled from any tier the client already picked on the public approval page. This defines the
  // recurring subscription's help-desk line. SwitchPilot needs no question — it's derived from the
  // quote's venues (shown read-only below).
  const [helpDeskTier, setHelpDeskTier] = useState<number>(proposal?.rnwyHelpDeskTier ?? 0);
  // item 786 (Chad) — SwitchPilot is the CLIENT'S CHOICE at approval (any sale type):
  // when the quote includes it, offer Include/Exclude (default Include). Not quoted = no
  // choice (it'd need its own quote for pricing).
  const [switchPilotDeclined, setSwitchPilotDeclined] = useState(false);
  const hasSwitchPilot = useMemo(() => {
    try {
      const jl = parseVenues(proposal?.rnwyVenuesJson ?? undefined).jobLevel;
      return (jl.switchPilot?.switchCount ?? 0) > 0;
    } catch { return false; }
  }, [proposal?.rnwyVenuesJson]);
  // item 789 (Chad) — priced Third-Party (module toggle + qty on the quote) is the client's
  // Include/Exclude call at approval too, exactly like SwitchPilot/Help Desk.
  const [thirdPartyDeclined, setThirdPartyDeclined] = useState(false);
  const hasThirdParty = useMemo(() => {
    try {
      const jl = parseVenues(proposal?.rnwyVenuesJson ?? undefined).jobLevel;
      return !!jl.modules?.thirdParty && ((jl as any).thirdPartyCount ?? 0) > 0;
    } catch { return false; }
  }, [proposal?.rnwyVenuesJson]);
  // dart (2026-07-15, Chad) — a STANDARD (non-RNWY) proposal that OFFERS Help Desk: the client must
  // choose one of four at approval — Decline / Gold / Platinum / Titanium (see the tier prices).
  const helpDeskPrices = useMemo((): { gold?: number; platinum?: number; titanium?: number } => {
    try { return proposal?.helpDeskTierPricesJson ? JSON.parse(proposal.helpDeskTierPricesJson) : {}; } catch { return {}; }
  }, [proposal?.helpDeskTierPricesJson]);
  const offersHelpDesk = !proposal?.isRnwy && !!proposal?.helpDeskTierPricesJson;
  const [helpDeskPicked, setHelpDeskPicked] = useState(false);
  // item 433 — deposit split chosen at approval ("like project leads"): quick picks
  // 25/50/100 + custom, default 50 (the historical 50/50)
  const [depositPercent, setDepositPercent] = useState('50');
  const depositPct = Math.min(100, Math.max(1, parseFloat(depositPercent) || 50));
  // item 611 — the customer's real PO number, captured on the wizard's first step.
  // OPTIONAL: a blank value never blocks approval (it can be added later).
  const [poNumber, setPoNumber] = useState('');
  // item 485 — seed the itemize toggle from the company setting (default OFF)
  const { defaults: companyDefaults } = useCompanyDefaults();
  const defaultItemize = companyDefaults?.itemizeByDefault ?? false;

  useEffect(() => {
    if (isOpen && proposal) {
      setStep(0);
      setBusy(false);
      setWorkOrder(undefined);
      setItemize(defaultItemize);
      setLeads([]);
      setRnwySaleType(0);
      setDepositPercent(String(proposal.depositPercent ?? 50)); // item 433
      setPoNumber(proposal.poNumber ?? ''); // item 611 — prefill any PO already on the job
    }
  }, [isOpen, proposal, defaultItemize]);

  // item 611 — the grid/project row may not carry the PO; once the detail lands, seed the
  // field from it, but only while the user hasn't typed anything yet (don't clobber input).
  useEffect(() => {
    if (isOpen && detail?.poNumber && poNumber === '') setPoNumber(detail.poNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, isOpen]);

  const {
    total: leadTotal, balanced: leadsBalanced, complete: leadsComplete,
    unique: leadsUnique, allPositive: leadsAllPositive,
  } = validateLeads(leads);
  // a proposal under the $1,000 commission floor pays ZERO commission (item 187), so the
  // project-lead step is pointless — only show it when the total is at/above the floor.
  // item 1143 (Chad) — the lead step ALWAYS shows: a small job still gets a lead for
  // accountability and the Sales Bible. The $1k floor only gates PAY — and (item 1149) the
  // authoritative floor is judged at invoice on the commission-flagged config-SKU lines.
  const leadsApply = true;
  const leadsLikelyPaid = commissionBase >= 1000;
  const addLead = () => setLeads([...leads, { userId: '', percent: leads.length === 0 ? 100 : 0 }]);
  const removeLead = (idx: number) => setLeads(leads.filter((_, i) => i !== idx));
  const setLeadUser = (idx: number, userId: string) => setLeads(
    leads.map((l, i) => (i === idx ? { ...l, userId } : l)),
  );
  const setLeadPercent = (idx: number, percent: number) => setLeads(
    leads.map((l, i) => (i === idx ? { ...l, percent } : l)),
  );

  if (!proposal) return null;

  // item 50: deposit/final invoices are named off the proposal title and split
  // 50/50 by the backend — show the deposit name as a read-only preview.
  const depositInvoiceNumber = depositInvoiceName(proposal);

  const dismiss = () => { if (!busy) onClose(); };

  const onApprove = async () => {
    if (leads.length && (!leadsComplete || !leadsUnique || !leadsAllPositive || !leadsBalanced)) {
      // item 555 — mirror the backend rules in order so the message is specific (a duplicate
      // user or a 0% lead used to slip past the 100% check and fail on a generic server error).
      let title = 'Project lead percentages must total 100%.';
      if (!leadsComplete) title = 'Choose a user for each project lead.';
      else if (!leadsUnique) title = 'Each project lead must be a different person.';
      else if (!leadsAllPositive) title = "Each project lead's percentage must be greater than 0.";
      toast({ status: 'error', title });
      return;
    }
    // item 240 — a RNWY proposal must commit to a sale type at approval (PO landed)
    if (proposal.isRnwy && rnwySaleType === 0) {
      toast({ status: 'error', title: 'Choose the RNWY sale type (perpetual, 2-year, or 5-year).' });
      return;
    }
    // dart (2026-07-15, Chad) — a standard quote that offers Help Desk requires an explicit choice
    // (one of four: Decline / Gold / Platinum / Titanium).
    if (offersHelpDesk && !helpDeskPicked) {
      toast({ status: 'error', title: 'Choose a Help Desk tier for the client — or Decline.' });
      return;
    }
    setBusy(true);
    try {
      // item 433 deposit split + item 611 customer PO (optional, never blocks)
      await approveProposal(proposal.id, leads, rnwySaleType, depositPct, poNumber, helpDeskTier, hasSwitchPilot && switchPilotDeclined, hasThirdParty && thirdPartyDeclined, noLeadCommission);
      // item 1145 — illustrations: Deployment task (assignee gets the existing task email)
      // + the Sales Bible stamp. Best-effort: never blocks the approval.
      if (illNeeded && illAssignee) {
        try {
          const apiUrl = process.env.REACT_APP_BASIX_API || '';
          const jobLabel = proposal.proposalTitle || proposal.proposalNumber || 'new job';
          const created = await axios.post(`${apiUrl}DepartmentTask`, {
            department: 2, // Deployment
            text: `Illustrations — ${jobLabel}`,
          });
          const taskId = (created.data as any)?.id ?? created.data;
          if (taskId) await axios.post(`${apiUrl}DepartmentTask/${taskId}/assignees`, { userIds: [illAssignee] });
          const assigneeName = users?.index.find((u: UserDto) => u.id === illAssignee)?.name ?? '';
          await axios.post(`${apiUrl}Proposal/SalesBible/${proposal.id}`, {
            // whole-set update — preserve the other manual columns
            platform: (proposal as any).platform ?? null,
            deploymentDate: (proposal as any).deploymentDate ?? null,
            projectLeadPayoutInput: (proposal as any).projectLeadPayoutInput ?? null,
            notes: (proposal as any).salesBibleNotes ?? null,
            illustrationsComplete: `Assigned — ${assigneeName}`.trim(),
          });
        } catch { /* the approval already succeeded; the task can be added by hand */ }
      }
      // item 448 — recurring commissions are no longer assigned at approval; they're
      // entered manually when creating a recurring invoice under the Recurring section.
      const wo = await getWorkOrderByProposal(proposal.id);
      setWorkOrder(wo);
      setStep(2); // → Work order step (item 611 inserted PO as step 0)
    } catch {
      toast({ status: 'error', title: 'The proposal could not be approved.' });
    } finally {
      setBusy(false);
    }
  };

  // item 1446 (Chad: "be louder about what step we are processing — deposit, 50%, final 50%") —
  // the plan for the invoice this wizard generates: nothing exists on the WO yet, so it is the
  // DEPOSIT at the chosen % (or the single FULL invoice at 100%). Drives the step block + labels.
  const depositPlan = workOrder
    ? computeInvoicePlan({
      total: workOrder.retailTotal ?? 0, proposalDriven: true, proposalDepositPercent: depositPct, prior: [],
    }, null)
    : undefined;
  const isSingleBill = depositPct >= 100;
  const generateLabel = isSingleBill ? 'Generate invoice (100% · paid in full)' : `Generate deposit invoice (${depositPct}%)`;

  const onGenerate = async () => {
    if (!workOrder) return;
    setBusy(true);
    try {
      // item 790 (Chad) — blank number = the backend consumes a real INV-#### from the
      // counter; the friendly "{title} - Deposit" is the invoice NAME (ServiceName), not the number.
      await generateDepositInvoice({ workOrderId: workOrder.id, invoiceNumber: '', itemize });
      // item 1446 — say what was made and what is still to come
      let madeTitle = 'Deposit invoice created.';
      if (depositPlan && isSingleBill) madeTitle = `Invoice created as a Draft — ${currency(depositPlan.billsNow)} (100%, paid in full).`;
      else if (depositPlan) madeTitle = `Deposit invoice (${depositPct}%) created as a Draft — ${currency(depositPlan.billsNow)}.`;
      toast({
        status: 'success',
        title: madeTitle,
        description: depositPlan && !isSingleBill
          ? `Finalize and send it. The FINAL (${100 - depositPct}% · ${currency(depositPlan.balanceAfter)}) is generated later from the work order — not now.`
          : 'Finalize and send it. Proposal is Approved Deposited.',
        duration: 9000,
        isClosable: true,
      });
      setEmailPrompt(true); // → prompt to send the thank-you (Chad: pop up after generating the invoice)
    } catch {
      toast({ status: 'error', title: 'The invoice was NOT created.' });
    } finally {
      setBusy(false);
    }
  };

  const onSendEmail = async () => {
    setBusy(true);
    try {
      await sendApprovalEmail(proposal.id, emailTo.trim() || undefined);
      toast({ status: 'success', title: 'Thank-you email sent.' });
    } catch {
      toast({ status: 'error', title: 'The email could not be sent.' });
    } finally {
      setBusy(false);
      setEmailPrompt(false);
      onClose();
    }
  };
  const onSkipEmail = () => { setEmailPrompt(false); onClose(); };

  return (
    <>
      <Modal isOpen={isOpen} onClose={dismiss} size="xl" closeOnOverlayClick={!busy} closeOnEsc={!busy}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={3}>
            {(proposal as any).clientApprovedDate ? 'PROCESS PROPOSAL' : 'APPROVE PROPOSAL'}
            <Progress
              value={((step + 1) / STEPS.length) * 100}
              size="xs"
              colorScheme="brandGreen"
              mt={3}
            />
            <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={2}>
              {`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]}`}
            </Text>
          </ModalHeader>
          <ModalBody minH="220px" py={5}>
            {/* item 611 — FIRST step: capture the customer's real PO number. OPTIONAL — the
              Next button is always enabled; a blank PO never blocks approval (add it later). */}
            {step === 0
          && (
            <Stack spacing={5}>
              <Text>
                Enter the customer&apos;s purchase-order number for
                {' '}
                <strong>{proposal.proposalTitle}</strong>
                {' '}
                (
                {proposal.proposalNumber}
                ).
              </Text>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Customer PO number</Text>
                <Input
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="PO number"
                  autoFocus
                />
              </Box>
              {/* item 797 (Chad) — upload the client's PO right here at approval. It attaches to
                  the PROPOSAL and rides the chain to the auto-created work order + its invoice, so
                  opening the proposal, WO, or invoice all show it. */}
              {proposal.id && proposal.id !== NIL && (
                <ReceivedPoAttachments docId={proposal.id} />
              )}
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Optional — you can leave the PO number blank and add it later, and upload the
                client&apos;s PO now or any time. It carries to the work order + invoice.
              </Alert>
            </Stack>
          )}
            {step === 1
          && (
            <Stack spacing={5}>
              <Text>
                {/* item 558 — with the client's OK already recorded, our side PROCESSES */}
                {(proposal as any).clientApprovedDate ? 'Processing' : 'Approving'}
                {' '}
                <strong>{proposal.proposalTitle}</strong>
                {' '}
                (
                {proposal.proposalNumber}
                ) creates a work order from its items so the job can begin.
              </Text>
              {(proposal as any).clientApprovedDate && (
                <Alert status="success" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {`The client already approved this proposal${(proposal as any).clientApprovedByName ? ` — signed ${(proposal as any).clientApprovedByName}` : ''}.`}
                </Alert>
              )}
              <Stack bg={cardBg} borderRadius="md" p={5} spacing={3}>
                <HStack justify="space-between">
                  <Text color="gray.500">Customer</Text>
                  <Text fontWeight="medium">{proposal.customerName ?? proposal.customer?.name}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Proposal total</Text>
                  {totalPending
                    ? <Spinner size="xs" label="Loading total" />
                    : <Text fontWeight="medium">{currency(displayTotal)}</Text>}
                </HStack>
              </Stack>
              {totalFailed && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  The proposal&apos;s items could not be loaded, so the total shown may be
                  wrong. Close the wizard and try again before approving.
                </Alert>
              )}
              {/* item 164/446 — THE FIRST QUESTION (Chad): who leads this job? Project-lead
                  commission pays ONLY the assigned lead(s), split by %; it is NOT paid to
                  everyone who carries a project-lead rate. One lead = 100%; co-leads split
                  to 100%. Hidden under the $1,000 commission floor (item 187). */}
              {leadsApply && (
              <Box borderWidth="1px" borderColor={leads.length > 0 && !(leadsBalanced && leadsUnique && leadsAllPositive) ? 'red.400' : cardBorder} borderRadius="md" p={4}>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="semibold">Who is the project lead?</Text>
                  {leads.length > 0 && (
                    <Badge colorScheme={leadsBalanced && leadsUnique && leadsAllPositive ? 'green' : 'red'} fontSize="0.75em">
                      {`Total ${leadTotal % 1 === 0 ? leadTotal : leadTotal.toFixed(2)}%`}
                      {/* item 555 — call out the specific failing rule, not just the total */}
                      {(() => {
                        if (!leadsUnique) return ' — duplicate lead';
                        if (!leadsAllPositive) return ' — 0% not allowed';
                        if (!leadsBalanced) return ' — must equal 100%';
                        return ' ✓';
                      })()}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  The assigned lead earns the project-lead commission on this job. Add a second
                  lead to split it — the percentages divide that commission and must total 100%.
                </Text>
                {/* item 1143 — below the floor the lead is RECORDED but pays nothing */}
                {!leadsLikelyPaid && (
                  <Text fontSize="xs" color="orange.300" mb={3} fontWeight="semibold">
                    Below the $1,000 commission floor — the lead is recorded for accountability
                    and the Sales Bible, but no project-lead commission will book (the floor is
                    judged on the config-SKU lines at invoice).
                  </Text>
                )}
                {/* item 1174 (Chad) — the SIGN, available at ANY amount: declare that this job
                    books no lead commission (the assignment above still records). */}
                <Checkbox
                  mb={3}
                  isChecked={noLeadCommission}
                  onChange={(e) => setNoLeadCommission(e.target.checked)}
                >
                  <Text fontSize="xs">
                    Book NO lead commission on this job — assignment recorded for accountability only
                  </Text>
                </Checkbox>
                {leads.map((lead, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <HStack key={idx} mb={2}>
                    <Select
                      flex="1"
                      placeholder="— Select lead —"
                      value={lead.userId}
                      onChange={(e) => setLeadUser(idx, e.target.value)}
                    >
                      {users?.index.map((u: UserDto) => (
                        // item 555 — a user already chosen as a lead in another row is disabled
                        // here so the same person can't be assigned twice (backend rejects it).
                        <option key={u.id} value={u.id} disabled={leads.some((l, i) => i !== idx && l.userId === u.id)}>{u.name}</option>
                      ))}
                    </Select>
                    <Flex align="center" w="110px">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="any"
                        textAlign="right"
                        onFocus={(e) => e.currentTarget.select()} // item 555 — select the sticky 0 on focus
                        value={Number.isFinite(lead.percent) ? lead.percent : 0}
                        onChange={(e) => setLeadPercent(idx, parseFloat(e.target.value) || 0)}
                      />
                      <Text ml={1} color="gray.500">%</Text>
                    </Flex>
                    <IconButton
                      aria-label="Remove lead"
                      icon={<CloseIcon boxSize={2.5} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => removeLead(idx)}
                    />
                  </HStack>
                ))}
                <Button size="sm" variant="outline" leftIcon={<AddIcon boxSize={2.5} />} onClick={addLead}>
                  {leads.length === 0 ? 'Assign project lead' : 'Add co-lead (split)'}
                </Button>
              </Box>
              )}
              {/* item 1145 (Chad) — illustrations become a Deployment task with an assignee */}
              <Box borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={4}>
                <Checkbox isChecked={illNeeded} onChange={(e) => setIllNeeded(e.target.checked)}>
                  <Text as="span" fontSize="sm" fontWeight="semibold">Illustrations needed</Text>
                </Checkbox>
                {illNeeded && (
                  <Select
                    mt={2}
                    placeholder="— assign illustrations to —"
                    value={illAssignee}
                    onChange={(e) => setIllAssignee(e.target.value)}
                  >
                    {users?.index.map((u: UserDto) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </Select>
                )}
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Creates a Deployment task assigned to them (they&apos;re emailed) and stamps the
                  Sales Bible&apos;s Illustrations column &quot;Assigned&quot;.
                </Text>
              </Box>
              {/* item 1151 (Chad) — the salaried PROJECT MANAGER: one flagged PM = shown here,
                  auto-assigned + emailed when the job processes. Flag PMs under Users. */}
              {(() => {
                const pms = (users?.index ?? []).filter((u: any) => u.isProjectManager);
                if (pms.length === 1) {
                  return (
                    <Text fontSize="sm" color="gray.500">
                      {`Project manager: ${pms[0].name} — assigned automatically and emailed when this job processes.`}
                    </Text>
                  );
                }
                if (pms.length > 1) {
                  return (
                    <Text fontSize="sm" color="orange.300">
                      {`${pms.length} project managers are flagged — the job won't auto-assign one; set the PM on the project after approval.`}
                    </Text>
                  );
                }
                return null;
              })()}
              {/* item 433 — deposit terms: what % the Deposit invoice bills (Final bills
                  the rest); 100% = single-invoice sale. Default 50/50. */}
              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" color="gray.500">Deposit terms</Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {`${depositPct}% now (${totalPending ? '…' : currency(proposalTotal * (depositPct / 100))})`}
                    {depositPct < 100 ? ` · ${100 - depositPct}% on final` : ' — paid in full'}
                  </Text>
                </HStack>
                <HStack>
                  {[25, 50, 100].map((p) => (
                    <Button
                      key={p}
                      size="sm"
                      variant={depositPct === p ? 'solid' : 'outline'}
                      onClick={() => setDepositPercent(String(p))}
                    >
                      {`${p}%`}
                    </Button>
                  ))}
                  <Input
                    size="sm"
                    maxW="90px"
                    type="number"
                    min={1}
                    max={100}
                    step="1"
                    textAlign="right"
                    value={depositPercent}
                    onChange={(e) => setDepositPercent(e.target.value)}
                    aria-label="Custom deposit percent"
                  />
                  <Text fontSize="sm" color="gray.500">%</Text>
                </HStack>
              </Box>
              {/* item 240 — RNWY proposals carry all 3 options; commit to one here at approval */}
              {proposal.isRnwy && (
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>RNWY sale type (now that the PO has landed)</Text>
                  <Select
                    placeholder="— Select sale type —"
                    value={rnwySaleType || ''}
                    onChange={(e) => {
                      const v = Number(e.target.value) || 0;
                      setRnwySaleType(v);
                      // item 698 (Chad) — subscriptions don't offer Gold (CFG rule); clear it if selected
                      if ((v === 2 || v === 3) && helpDeskTier === 1) setHelpDeskTier(0);
                    }}
                  >
                    <option value={1}>Perpetual</option>
                    <option value={2}>2-Year Subscription</option>
                    <option value={3}>5-Year Subscription</option>
                  </Select>
                  {/* dart (2026-07-15, Chad) — Phase A: Help Desk yes/no → tier. Defines the recurring
                      help-desk line. Perpetual has no monthly license but still bills Help Desk monthly. */}
                  <Text fontSize="sm" color="gray.500" mt={3} mb={1}>Help Desk (monthly add-on)</Text>
                  <Select
                    value={helpDeskTier || ''}
                    placeholder="No Help Desk"
                    onChange={(e) => setHelpDeskTier(Number(e.target.value) || 0)}
                  >
                    {/* item 698 (Chad) — Gold is perpetual-only; 2yr/5yr subscriptions offer Platinum/Titanium */}
                    {rnwySaleType !== 2 && rnwySaleType !== 3 && <option value={1}>Gold</option>}
                    <option value={2}>Platinum</option>
                    <option value={3}>Titanium</option>
                  </Select>
                  {/* item 786 (Chad) — quoted SwitchPilot = the client's Include/Exclude call */}
                  {hasSwitchPilot ? (
                    <>
                      <Text fontSize="sm" color="gray.500" mt={3} mb={1}>SwitchPilot (monthly add-on, quoted)</Text>
                      <Select
                        value={switchPilotDeclined ? 'no' : 'yes'}
                        onChange={(e) => setSwitchPilotDeclined(e.target.value === 'no')}
                      >
                        <option value="yes">Include SwitchPilot — one-time config + bills monthly</option>
                        <option value="no">Exclude SwitchPilot — client declined</option>
                      </Select>
                    </>
                  ) : (
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      SwitchPilot: not in this quote. Quoted independently if added later.
                    </Text>
                  )}
                  {/* item 789 (Chad) — quoted priced Third-Party = the client's call too */}
                  {hasThirdParty && (
                    <>
                      <Text fontSize="sm" color="gray.500" mt={3} mb={1}>Third-Party integrations (monthly add-on, quoted)</Text>
                      <Select
                        value={thirdPartyDeclined ? 'no' : 'yes'}
                        onChange={(e) => setThirdPartyDeclined(e.target.value === 'no')}
                      >
                        <option value="yes">Include Third-Party integrations — bills monthly</option>
                        <option value="no">Exclude Third-Party integrations — client declined</option>
                      </Select>
                    </>
                  )}
                </Box>
              )}
              {/* dart (2026-07-15, Chad) — STANDARD quote that OFFERS Help Desk: the client must pick
                  one of four (Decline / Gold / Platinum / Titanium). One box is required. */}
              {offersHelpDesk && (
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>Help Desk — the client chooses one (required)</Text>
                  <Select
                    placeholder="— Choose Help Desk —"
                    value={helpDeskPicked ? String(helpDeskTier) : ''}
                    onChange={(e) => { setHelpDeskTier(Number(e.target.value) || 0); setHelpDeskPicked(true); }}
                  >
                    <option value={0}>Decline Help Desk</option>
                    {helpDeskPrices.gold != null && <option value={1}>{`Gold — $${helpDeskPrices.gold.toFixed(2)}/mo`}</option>}
                    {helpDeskPrices.platinum != null && <option value={2}>{`Platinum — $${helpDeskPrices.platinum.toFixed(2)}/mo`}</option>}
                    {helpDeskPrices.titanium != null && <option value={3}>{`Titanium — $${helpDeskPrices.titanium.toFixed(2)}/mo`}</option>}
                  </Select>
                  {helpDeskPicked && helpDeskTier > 0 && (
                    <Text fontSize="xs" color="orange.400" mt={2}>
                      This is a monthly fee for two years — see terms.
                    </Text>
                  )}
                </Box>
              )}
              {/* item 448 — recurring commissions were removed from the approval flow;
                  they're entered manually when creating a recurring invoice. */}
              {/* item 187 (2026-06-26): Developing Partner is no longer assigned here — it's
                  automatic (every user with a Developing-Partner % earns on RNWY-SYS CONFIG
                  as it's invoiced). Commission accrues at invoice FINALIZATION, not approval. */}
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Once approved, the proposal becomes view-only. Duplicate it later to start a revision.
              </Alert>
            </Stack>
          )}
            {step === 2 && workOrder
          && (
            <Stack spacing={5}>
              <Flex align="center" color="green.400">
                <Box as={FaCheckCircle} mr={2} />
                <Heading size="sm">Work order created</Heading>
              </Flex>
              <Stack bg={cardBg} borderRadius="md" p={5} spacing={3}>
                <HStack justify="space-between">
                  <Text color="gray.500">Number</Text>
                  <Text fontWeight="medium">{workOrder.workOrderNumber}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Title</Text>
                  <Text fontWeight="medium">{workOrder.workOrderTitle}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Line items</Text>
                  <Text fontWeight="medium">{workOrder.workOrderItems?.length ?? 0}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Total</Text>
                  <Text fontWeight="medium">{currency(workOrder.retailTotal)}</Text>
                </HStack>
              </Stack>
              {/* item 1446 — say which invoice the next step makes */}
              <Text fontSize="sm" color="gray.500">
                {isSingleBill
                  ? 'Next: the single invoice (100% — paid in full). Generate it now, or later from Work Orders.'
                  : `Next: STEP 1 OF 2 — the DEPOSIT invoice (${depositPct}%). The FINAL (${100 - depositPct}%) comes later from Work Orders. Generate the deposit now, or do it later.`}
              </Text>
            </Stack>
          )}
            {step === 3 && workOrder
          && (
            <Stack spacing={5}>
              {/* item 1446 — the loud step block replaces the hard-coded "50%" copy */}
              <InvoiceStepBanner plan={depositPlan} />
              <Text fontSize="sm" color="gray.500">
                This invoice draws from work order
                {' '}
                {workOrder.workOrderNumber}
                . Creating it marks the proposal Approved Deposited.
              </Text>
              <Stack bg={cardBg} borderRadius="md" p={5} spacing={3}>
                <HStack justify="space-between">
                  <Text color="gray.500">Invoice</Text>
                  <Text fontWeight="medium">{depositInvoiceNumber}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">{isSingleBill ? 'Bills now (100%)' : `Deposit (${depositPct}%)`}</Text>
                  <Text fontWeight="medium">{currency(depositPlan?.billsNow ?? 0)}</Text>
                </HStack>
                {!isSingleBill && (
                  <HStack justify="space-between">
                    <Text color="gray.500">{`Final (${100 - depositPct}%) — later`}</Text>
                    <Text fontWeight="medium" opacity={0.7}>{currency(depositPlan?.balanceAfter ?? 0)}</Text>
                  </HStack>
                )}
              </Stack>
              <Checkbox isChecked={itemize} onChange={(e) => setItemize(e.target.checked)}>
                Itemize lines (group price)
              </Checkbox>
            </Stack>
          )}
          </ModalBody>
          <ModalFooter>
            {/* item 611 — PO step: Cancel + Next (always enabled; PO is optional) */}
            {step === 0
          && (
            <>
              <Button variant="ghost" mr={3} onClick={dismiss}>Cancel</Button>
              <Button onClick={() => setStep(1)}>Next</Button>
            </>
          )}
            {step === 1
          && (
            <>
              <Button variant="ghost" mr={3} onClick={() => setStep(0)} isDisabled={busy}>Back</Button>
              <Button
                onClick={onApprove}
                isLoading={busy}
                loadingText={(proposal as any).clientApprovedDate ? 'Processing' : 'Approving'}
              >
                {(proposal as any).clientApprovedDate
                  ? 'Process & create work order' : 'Approve & create work order'}
              </Button>
            </>
          )}
            {step === 2
          && (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>Done — invoice later</Button>
              <Button onClick={() => setStep(3)}>{isSingleBill ? 'Generate invoice' : 'Generate deposit invoice'}</Button>
            </>
          )}
            {step === 3
          && (
            <>
              <Button variant="ghost" mr={3} onClick={() => setStep(2)} isDisabled={busy}>Back</Button>
              <Button onClick={onGenerate} isLoading={busy} loadingText="Generating">
                {generateLabel}
              </Button>
            </>
          )}
            {busy && step === 1 && <Spinner ml={3} size="sm" />}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* dart (2026-07-14) — the thank-you email prompt, shown after the deposit invoice is
        generated. Skip, or send to the customer's email on file (blank) or a "send elsewhere"
        address. */}
      <Modal isOpen={emailPrompt} onClose={onSkipEmail} size="md" closeOnOverlayClick={!busy} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={2}>Send the approval thank-you?</ModalHeader>
          <ModalBody py={4}>
            <Text fontSize="sm" color="gray.500" mb={3}>
              Email the client a thank-you with the approved proposal PDF attached. Leave the
              recipient blank to use the customer&apos;s email on file, or send it elsewhere.
            </Text>
            <Text fontSize="sm" color="gray.500" mb={1}>Recipient</Text>
            <Input
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="Customer's email on file"
              type="email"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSkipEmail} isDisabled={busy}>Skip</Button>
            <Button colorScheme="brandGreen" onClick={onSendEmail} isLoading={busy}>Send email</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

ProposalApprovalWizard.defaultProps = { proposal: undefined };

export default ProposalApprovalWizard;
